/* eslint-disable @typescript-eslint/no-explicit-any */
import { cn } from '@bem-react/classname'
import { FC, ReactNode, useEffect, useState } from 'react'
import DatePickerField from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Transition } from 'react-transition-group'
import { v4 as uuidv4 } from 'uuid'

import './DatePicker.scss'

type DatePickerType = 'filter'
type LabelColorType = 'dark' | 'blue'
type FlexDirectionType = 'column' | 'row'
type alignItemsType = 'top' | 'bottom' | 'right' | 'left' | 'middle'

const cnDatePicker = cn('DatePicker')

export interface DatePickerSchema {
  uid?: string
  label?: string
  name?: string
  register?: any
  errors?: string
  placeholder?: string
  required?: boolean
  readonly?: boolean
  disabled?: boolean
  className?: string
  classNameLabel?: string
  classNameInput?: string
  control?: any
  iconLeft?: ReactNode | string
  iconRight?: ReactNode | string
  dateFormat?: string
  defaultDate?: Date
  isDefaultDate?: boolean
  isButtonOpen?: boolean
  showTimeSelect?: boolean
  showTimeSelectOnly?: boolean
  timeIntervals?: number
  timeCaption?: string
  showTimeInput?: boolean
  onChange?: (e: Date | [Date | null, Date | null] | null) => void
  value?: string | Date
  color?: 'blue' | 'default'
  disabledColor?: 'blue' | 'default'
  textInputCenter?: boolean
  excludeDates?: Date[]
  minDate?: Date
  maxDate?: Date
  small?: boolean
  type?: DatePickerType
  flexDirection?: FlexDirectionType
  labelColor?: LabelColorType
  alignItems?: alignItemsType
  fluid?: boolean
  filterTime?(date: Date): boolean
  timeFormat?: string
}

export const DatePicker: FC<DatePickerSchema> = ({
  uid = 'datepicker-' + uuidv4(),
  label,
  name,
  required,
  errors,
  placeholder,
  disabled,
  className,
  classNameLabel,
  classNameInput,
  control,
  iconLeft,
  iconRight,
  dateFormat = 'yyyy/MM/dd',
  defaultDate,
  showTimeSelect,
  showTimeSelectOnly,
  timeIntervals,
  timeCaption = 'Time',
  showTimeInput,
  isButtonOpen,
  onChange,
  value,
  color,
  disabledColor,
  textInputCenter,
  excludeDates,
  minDate,
  maxDate,
  small,
  type,
  flexDirection = 'column',
  alignItems = 'left',
  fluid = false,
  filterTime,
  timeFormat,
}) => {
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [open, setOpen] = useState<boolean>(false)
  const { t } = useTranslation('locales')

  useEffect(() => {
    if (defaultDate) setStartDate(defaultDate)
  }, [defaultDate])

  return (
    <div
      className={cnDatePicker(
        {
          error: !!errors,
          column: flexDirection === 'column',
          row: flexDirection === 'row',
          top: alignItems === 'top',
          bottom: alignItems === 'bottom',
          left: alignItems === 'left',
          right: alignItems === 'right',
          middle: alignItems === 'middle',
        },
        [className],
      )}
    >
      {!!label && (
        <label
          htmlFor={uid}
          className={cnDatePicker(
            'label',
            {
              required,
              filter: type === 'filter',
            },
            [classNameLabel],
          )}
        >
          {label}
        </label>
      )}
      <div className={cnDatePicker('wrap')}>
        {iconLeft && (
          <div
            className={cnDatePicker('icon', {
              left: true,
              click: isButtonOpen,
            })}
            onClick={() => setOpen(true)}
          >
            {iconLeft}
          </div>
        )}

        {control ? (
          <Controller
            control={control}
            name={name || ''}
            render={({ field: { onChange, value } }) => (
              <DatePickerField
                id={uid}
                // locale='ru-RU'
                disabled={disabled}
                dateFormat={dateFormat}
                placeholderText={placeholder}
                onChange={onChange}
                selected={value ? new Date(value) : startDate}
                className={cnDatePicker(
                  'input',
                  {
                    error: !!errors,
                    blue: color === 'blue',
                    center: textInputCenter,
                    disabled: disabledColor,
                    small,
                    filter: type === 'filter',
                  },

                  [classNameInput],
                )}
                calendarClassName={cnDatePicker('calendar', {
                  fluid,
                })}
                clearButtonClassName={cnDatePicker('clear')}
                dayClassName={() => cnDatePicker('day')}
                weekDayClassName={() => cnDatePicker('week')}
                monthClassName={() => cnDatePicker('month')}
                timeClassName={() => cnDatePicker('time')}
                popperClassName={cnDatePicker('popper', { fluid })}
                wrapperClassName={cnDatePicker('wrapper')}
                showTimeSelect={showTimeSelect}
                showTimeSelectOnly={showTimeSelectOnly}
                timeIntervals={timeIntervals}
                timeCaption={t(timeCaption)}
                showTimeInput={showTimeInput}
                excludeDates={excludeDates}
                minDate={minDate}
                maxDate={maxDate}
                open={isButtonOpen && open}
                onClickOutside={() => isButtonOpen && setOpen(false)}
                onBlur={() => isButtonOpen && setOpen(false)}
                onFocus={() => isButtonOpen && setOpen(true)}
                filterTime={filterTime}
                timeFormat={timeFormat}
              />
            )}
          />
        ) : (
          <DatePickerField
            id={uid}
            // locale='ru-RU'
            disabled={disabled}
            dateFormat={dateFormat}
            placeholderText={placeholder}
            onChange={(e: any) => {
              onChange && onChange(e)
            }}
            selected={value ? new Date(value) : startDate}
            className={cnDatePicker(
              'input',
              {
                error: !!errors,
                blue: color === 'blue',
                center: textInputCenter,
                filter: type === 'filter',
              },
              [classNameInput],
            )}
            calendarClassName={cnDatePicker('calendar', { fluid })}
            clearButtonClassName={cnDatePicker('clear')}
            dayClassName={() => cnDatePicker('day')}
            weekDayClassName={() => cnDatePicker('week')}
            monthClassName={() => cnDatePicker('month')}
            timeClassName={() => cnDatePicker('time')}
            popperClassName={cnDatePicker('popper', { fluid })}
            wrapperClassName={cnDatePicker('wrapper')}
            showTimeSelect={showTimeSelect}
            showTimeSelectOnly={showTimeSelectOnly}
            timeIntervals={timeIntervals}
            timeCaption={t(timeCaption)}
            showTimeInput={showTimeInput}
            excludeDates={excludeDates}
            minDate={minDate}
            maxDate={maxDate}
            open={isButtonOpen && open}
            onClickOutside={() => isButtonOpen && setOpen(false)}
            onBlur={() => isButtonOpen && setOpen(false)}
            onFocus={() => isButtonOpen && setOpen(true)}
            filterTime={filterTime}
            timeFormat={timeFormat}
          />
        )}

        {iconRight && (
          <div
            className={cnDatePicker('icon', {
              right: true,
              click: isButtonOpen,
            })}
            onClick={() => setOpen(true)}
          >
            {iconRight}
          </div>
        )}
      </div>
      <Transition in={!!errors} timeout={500} mountOnEnter unmountOnExit>
        {state => (
          <div className={cnDatePicker('error', [state])}>{errors}</div>
        )}
      </Transition>
    </div>
  )
}
